import React from "react";
import { defaultWebchatOptions, IAppState, WebchatOptions } from "../../Redux/reduxState";
import { connect } from "react-redux";
import WebChat from "./webChat";
import { mergeStyles } from '@fluentui/merge-styles';
import { actionTypes, genericSingleAction } from "../../Redux/actions";
import { Dispatch, AnyAction } from 'redux';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { EditorModeTabs, ModeTab } from "./editorModeTabs";
import { ChatRegular, SoundWaveCircleSparkleRegular } from "@fluentui/react-icons";
import WebChatJsonEditor, { transformWebchatOptions } from "./webChatJsonEditor";
import { Category, EditorMode, VoiceCategory } from "../../utilities/types";
import { CategoryLink } from "./categoryLink";
import ConfigurationEntryViewer from "./configurationEntryViewer";
import { VoiceCategoryLink } from "./voiceCategoryLink";
import VoiceConfigurationEntryViewer from "./voiceConfigurationEntryViewer";
import { SipNotConfiguredWarning } from "./sipNotConfiguredWarning";
import { diff } from 'deep-object-diff';

const rootContainer = mergeStyles(
    {
        //use the base font-family from theme project
        fontFamily: 'var(--druid-font-family-base) !important',

    }
);
const headerContainer = mergeStyles(
    {
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--druid-grey-300)',
        padding: 'var(--druid-space-10) var(--druid-space-24)',
    }
);


const headerSeparator = mergeStyles(
    {
        width: 1,
        height: 8,
        backgroundColor: 'var(--druid-grey-500)'
    }
);

const headerSubtitle = mergeStyles(
    {
        fontSize: 13,
        color: 'var(--druid-grey-500)'
    }
);

const documentationLink = mergeStyles(
    {
        cursor: 'pointer',
        alignSelf: 'center'
    }
);

const editorModeContainer = mergeStyles(
    {
        marginLeft: 20
    }
);

const editorModeTabs: ModeTab<EditorMode>[] = [
    { key: EditorMode.Webchat, text: 'Chat', icon: <ChatRegular /> },
    { key: EditorMode.VoiceSnippet, text: 'Voice', icon: <SoundWaveCircleSparkleRegular /> },
];

const editorContainer = mergeStyles(
    {
        '.ms-Pivot': { // tab container
            display: 'flex',
            paddingLeft: 'var(--druid-space-24)',
            borderBottom: `1px solid var(--druid-color-border)`,
            flex: 1,
            '.ms-Pivot-link': {
                height: 'auto',
                marginRight: 'var(--druid-space-8)',
                borderRadius: 0,
                color: 'var(--druid-color-text-secondary)',
                padding: 'var(--druid-space-4) var(--druid-space-12)',
                background: 'transparent !important',
                '.ms-Button-flexContainer': {
                    '.ms-Pivot-text': {
                        fontSize: 'var(--druid-font-size-xl)',
                    },
                },
                ':hover': {
                    color: 'var(--druid-color-text-primary)',
                },
                ':before': {
                    left: 0,
                    right: 0,
                    height: 'var(--webchat-editor-tab-indicator-size)',
                    borderRadius: 'var(--webchat-editor-tab-indicator-radius)',
                },
            },
            '.ms-Pivot-link.is-selected': {
                color: 'var(--druid-color-text-primary)',
                fontWeight: 'var(--druid-font-weight-semibold)',
                ':before': {
                    backgroundColor: 'var(--druid-color-success-primary)',
                },
            }

        },
    }
);

const configurationEntriesContainer = mergeStyles(
    {
        paddingLeft: '25px !important',
        paddingTop: 'var(--druid-space-24) !important',
        height: 'calc(100vh - var(--webchat-editor-chrome-height))',
        maxHeight: 'calc(100vh - var(--webchat-editor-chrome-height))',
        // backgroundColor: 'purple !important',
        overflowY: 'auto',
        overscrollBehavior: 'none',
        // Reserve the scrollbar track even when the panel is short enough not to
        // scroll, so its contents do not shift left the moment a scrollbar appears.
        scrollbarGutter: 'stable',
        '.ms-Checkbox.is-checked': {

            '.ms-Checkbox-checkbox': {
                background: 'var(--druid-color-success-primary)',
                borderColor: `var(--druid-color-success-primary) !important`,
            }
        },
        '.ms-Checkbox-checkbox': {
            border: '1px solid var(--druid-color-border-strong)',
        },
        '.ms-TextField-fieldGroup': {
            borderRadius: 4,
            border: '1px solid var(--druid-color-border-strong)',
            ':hover, ::after': {
                border: `1px solid var(--druid-color-success-primary)`,
                borderRadius: 4
            },
            '.ms-TextField-prefix': {
                borderRadius: 4
            }
            // border: '1px solid red',
        },
        '.ms-Dropdown': {
            '.ms-Dropdown-title': {
                borderRadius: 4,
                border: '1px solid var(--druid-color-border-strong)',

            },
            '::after': {
                border: `2px solid var(--druid-color-success-primary)`,
                borderRadius: 4
            },
        },
        '.ms-Button--primary': {
            background: 'var(--druid-color-success-primary)',
            borderColor: `var(--druid-color-success-primary) !important`,
            ':hover, :active': {
                background: 'var(--druid-color-success-hover)',
                borderColor: `var(--druid-color-success-hover) !important`

            }
        }

    }
);

// Left column: the vertical tablist, separated from the entries by a divider.
const configurationCategoriesColumn = mergeStyles(
    {
        paddingLeft: 'var(--druid-space-24) !important',
        paddingRight: 'var(--druid-space-8) !important',
        maxWidth: '280px',
        paddingTop: 'var(--druid-space-24) !important',
        borderRight: '1px solid var(--druid-color-border)',
    }
);

// Vertical tablist holding the category links.
const configurationCategoriesList = mergeStyles(
    {
        display: 'flex',
        flexDirection: 'column',
        rowGap: 'var(--druid-space-2)',
    }
);


////////////////// WebChatEditor //////////////////

interface StateProps {
    activeCategory: Category;
    initialized: boolean;
    currentStyleOptions: Partial<WebchatOptions>;
    editorMode: EditorMode;
    activeVoiceCategory: VoiceCategory;
    isSipEnabled?: boolean;
}

interface DispatchProps {
    updateStyleElement: (styleElementName: string, value: any) => void,
    updateRootStateVariable: (stateVariableName: string, value: any) => void,
}

interface Props {
    botId: string;
    baseUrl: string;
    botName: string;
    documentationUrl: string;
    onClose: () => any;
    onSave: (json: any) => any;
    onSuccess: () => any;
    onConfigureSip?: () => any;
}

type PropsType = StateProps & DispatchProps & Props;

export class WebChatEditor extends React.Component<PropsType> {
    constructor(props: PropsType) {
        super(props);
    }


    private RenderCategories = () => {
        let resultingUIComponents = [];

        const { activeCategory } = this.props;

        for (let category in Category) {
            resultingUIComponents.push(
                <CategoryLink
                    key={category}
                    name={category}
                    activeCategory={activeCategory}
                    updateRootStateVariable={this.props.updateRootStateVariable}
                />
                
            );
        }

        return resultingUIComponents;
    }

    private RenderVoiceCategories = () => {
        let resultingUIComponents = [];

        const { activeVoiceCategory } = this.props;

        for (let category in VoiceCategory) {
            resultingUIComponents.push(
                <VoiceCategoryLink
                    key={category}
                    name={category}
                    activeVoiceCategory={activeVoiceCategory}
                    updateRootStateVariable={this.props.updateRootStateVariable}
                />
            );
        }

        return resultingUIComponents;
    }

    render() {
        const showSipWarning = this.props.isSipEnabled === false
            && (this.props.editorMode === EditorMode.VoiceSnippet || this.props.activeCategory === Category.Voice);

        return (
            <div
                className={`ms-Grid ${rootContainer}`}
                dir="ltr"
                style={showSipWarning
                    ? ({
                        '--webchat-editor-warning-height': '56px',
                        '--webchat-editor-chrome-height': 'calc(var(--webchat-editor-chrome-base-height) + var(--webchat-editor-warning-height))'
                    } as React.CSSProperties)
                    : undefined}
            >
                <div className={`ms-Grid-row ${headerContainer}`}>
                    <div className="flex-grow-1 d-flex align-items-center">
                        <h5 className="fw-semibold mb-2 mt-2 text-dark">{this.props.botName}</h5>
                        <div className={`subheader-separator subheader-separator-ver mx-2 my-auto ${headerSeparator}`}></div>
                        <div className="d-flex align-items-center fw-normal"><span className={`me-1 ${headerSubtitle}`}>{this.props.editorMode === EditorMode.VoiceSnippet ? 'Voice' : 'Chat'} customizations options</span></div>
                        <a title="Documentation" className={`help-actions-icon page-help-icon ms-2 ${documentationLink}`} href={this.props.documentationUrl} target="_blank" rel="noopener noreferrer" onClick={() => {

                        }}>
                            <svg  xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512">
                                <path  fill="currentColor" d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 448c-110.532 0-200-89.431-200-200 0-110.495 89.472-200 200-200 110.491 0 200 89.471 200 200 0 110.53-89.431 200-200 200zm107.244-255.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C184.846 131.491 214.94 112 261.794 112c49.071 0 101.45 38.304 101.45 88.8zM298 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z"></path>
                            </svg>
                            </a>
                        <div className={`d-flex align-items-center ${editorModeContainer}`}>
                            <EditorModeTabs
                                ariaLabel="Editor mode"
                                tabs={editorModeTabs}
                                size="md"
                                selectedKey={this.props.editorMode}
                                onChange={mode => {
                                    this.props.updateRootStateVariable('navigationTarget', undefined);
                                    this.props.updateRootStateVariable('editorMode', mode);
                                }}
                            />
                        </div>
                    </div>
                    <div className="right">
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn btn-primary" onClick={() => setTimeout(() => this.props.onSave(this.props.currentStyleOptions), 0)}> Save  </button>
                            <button type="button" className="btn btn-secondary" onClick={this.props.onClose}> Cancel </button></div>
                    </div>
                </div>

                {showSipWarning &&
                    <div className="ms-Grid-row pt-2 px-4">
                        <SipNotConfiguredWarning onConfigureSip={this.props.onConfigureSip} />
                    </div>}

                <div className={`ms-Grid-row webchat-editor-container ${editorContainer}`}>
                    <div className="ms-sm12 ms-md12 ms-lg12">
                        <Pivot aria-label="Large Link Size Pivot Example">
                            <PivotItem headerText="GUI">
                                {
                                    this.props.initialized && <div className="ms-lg12">
                                        <div className={`ms-Grid-col ms-sm3 ms-md3 ms-lg3 ${configurationEntriesContainer} ${configurationCategoriesColumn}`}>
                                            <div role="tablist" aria-orientation="vertical" aria-label="Customization categories" className={configurationCategoriesList}>
                                                {this.props.editorMode === EditorMode.VoiceSnippet ? this.RenderVoiceCategories() : this.RenderCategories()}
                                            </div>
                                        </div>
                                        <div className={`ms-Grid-col ms-sm9 ms-md9 ms-lg9 ${configurationEntriesContainer}`}>
                                            {
                                                this.props.editorMode === EditorMode.VoiceSnippet
                                                    ? <VoiceConfigurationEntryViewer updateRootStateVariable={this.props.updateRootStateVariable} />
                                                    : <ConfigurationEntryViewer updateRootStateVariable={this.props.updateRootStateVariable} />
                                            }
                                        </ div>
                                    </div>
                                }
                            </PivotItem>
                            <PivotItem headerText="Json Viewer">
                                <WebChatJsonEditor onSuccess={this.props.onSuccess} />
                            </PivotItem>
                        </Pivot>
                    </div>
                    <WebChat {...this.props} />
                </div>
            </div>
        );
    }
}

////////////////// Redux //////////////////

const mapStateToProps = (state: IAppState, ownProps: Props): StateProps => ({
    activeCategory: state.activeCategory,
    initialized: state.initialized,
    currentStyleOptions: { ...diff(defaultWebchatOptions(), transformWebchatOptions(state.webchatOptions)), customDruidStyleSet: state.webchatOptions.customDruidStyleSet },
    editorMode: state.editorMode,
    activeVoiceCategory: state.activeVoiceCategory,
    isSipEnabled: state.webchatOptions.voiceSettings?.isEnabled,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): DispatchProps => ({
    updateStyleElement: (styleElementName: string, value: any) => {
        dispatch(genericSingleAction<any>(actionTypes.UPDATE_STYLE_ELEMENT, { styleElementName: styleElementName, value: value }));
    },
    updateRootStateVariable: (stateVariableName: string, value: any) => {
        dispatch(genericSingleAction<any>(actionTypes.UPDATE_ROOT_WEBCHAT_STATE_VARIABlE, { propertyName: stateVariableName, value: value }));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(WebChatEditor);